import { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Syringe,
  Plus,
  AlertTriangle,
  Trash2,
  Save,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import type { PetFormData, VacinaEntry } from "@/pages/CadastrarPet";

type VacinaEntryComDelete = VacinaEntry & {
  _delete?: number;
};

interface Props {
  formData: PetFormData;
  updateField: <K extends keyof PetFormData>(field: K, value: PetFormData[K]) => void;
}

type VacinaCatalogo = {
  id: number | string;
  nome: string;
  intervalo_dias?: number;
};

const OPCAO_OUTRA = "outra";

const catalogoLocalPorEspecie: Record<string, VacinaCatalogo[]> = {
  cachorro: [
    { id: 1, nome: "V8 / V10", intervalo_dias: 21 },
    { id: 2, nome: "Raiva", intervalo_dias: 365 },
    { id: 3, nome: "Giárdia", intervalo_dias: 365 },
    { id: 4, nome: "Gripe", intervalo_dias: 365 },
  ],
  gato: [
    { id: 5, nome: "V3", intervalo_dias: 21 },
    { id: 6, nome: "V4", intervalo_dias: 21 },
    { id: 7, nome: "V5", intervalo_dias: 21 },
    { id: 2, nome: "Raiva", intervalo_dias: 365 },
  ],
};

function getPetIdDaUrl() {
  const match = window.location.pathname.match(/\/pets\/(\d+)\/(editar|edit)/);
  return match?.[1] || null;
}

function getStatus(data?: string) {
  if (!data) return { variant: "outline" as const, text: "Sem data" };

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  const d = new Date(`${data}T00:00:00`);

  if (d < hoje) return { variant: "destructive" as const, text: "Atrasada" };

  const diff = Math.ceil((d.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));
  if (diff <= 30) return { variant: "secondary" as const, text: "Próxima" };

  return { variant: "default" as const, text: "Em dia" };
}

function somarDias(data: string, dias: number) {
  if (!data || !dias) return "";

  const d = new Date(`${data}T00:00:00`);
  d.setDate(d.getDate() + dias);

  const ano = d.getFullYear();
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");

  return `${ano}-${mes}-${dia}`;
}

export default function CarteiraVacinas({ formData, updateField }: Props) {
  const [abertas, setAbertas] = useState<Set<string>>(new Set());
  const [salvando, setSalvando] = useState(false);

  const petId = getPetIdDaUrl();

  const catalogo = useMemo(() => {
    if (formData.tipo_pet === "cachorro") return catalogoLocalPorEspecie.cachorro;
    if (formData.tipo_pet === "gato") return catalogoLocalPorEspecie.gato;
    return [];
  }, [formData.tipo_pet]);

  const todasVacinas = (formData.vacinas || []) as VacinaEntryComDelete[];
  const vacinasVisiveis = todasVacinas.filter((v) => Number(v._delete ?? 0) !== 1);
  const vazio = vacinasVisiveis.length === 0;

  function getNomeVacina(vacina: VacinaEntryComDelete) {
    if (vacina.vacina_nome_custom?.trim()) {
      return vacina.vacina_nome_custom.trim();
    }

    const encontrada = catalogo.find(
      (item) => String(item.id) === String(vacina.vacina_id)
    );

    return encontrada?.nome || vacina.nome || "Vacina";
  }

  function getIntervalo(vacinaId: string | number | null | undefined) {
    const encontrada = catalogo.find((item) => String(item.id) === String(vacinaId));
    return encontrada?.intervalo_dias || 365;
  }

  function getValorSelectVacina(vacina: VacinaEntryComDelete) {
    if (vacina.vacina_id == null && vacina.vacina_nome_custom !== null) return OPCAO_OUTRA;
    if (vacina.vacina_id != null) return String(vacina.vacina_id);
    return "";
  }

  const toggleGrupo = (id: string) => {
    setAbertas((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const adicionarVacina = () => {
    const primeira = catalogo[0];

    const nova: VacinaEntry = {
      id: crypto.randomUUID(),
      vacina_id: primeira?.id ? Number(primeira.id) : null,
      vacina_nome_custom: null,
      nome: primeira?.nome || "",
      dose_num: vacinasVisiveis.length + 1,
      data_aplicacao: "",
      data_revacinar: "",
      fabricante: "",
      lote: "",
      via: "",
      peso: "",
      veterinario: "",
      nome_veterinario: "",
      validado: 0,
    };

    updateField("vacinas", [...todasVacinas, nova] as PetFormData["vacinas"]);
    setAbertas((prev) => new Set(prev).add(String(nova.id)));
  };

  const removerVacina = (id: string) => {
    const atualizadas = todasVacinas
      .map((v) => {
        if (String(v.id) !== String(id)) return v;

        const idNumerico = /^\d+$/.test(String(v.id));
        if (idNumerico) {
          return { ...v, _delete: 1 };
        }

        return null;
      })
      .filter(Boolean) as VacinaEntryComDelete[];

    updateField("vacinas", atualizadas as PetFormData["vacinas"]);

    setAbertas((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const atualizarVacina = (
    id: string,
    field: keyof VacinaEntry,
    value: string | number | null
  ) => {
    updateField(
      "vacinas",
      todasVacinas.map((v) => {
        if (String(v.id) !== String(id)) return v;

        const atualizado: VacinaEntryComDelete = { ...v, [field]: value };

        const vacinaId = atualizado.vacina_id;
        const dataAplicacao = atualizado.data_aplicacao || "";

        if (field === "vacina_id" && value) {
          const encontrada = catalogo.find((item) => String(item.id) === String(value));
          atualizado.nome = encontrada?.nome || "";
          atualizado.vacina_nome_custom = null;
        }

        if (field === "vacina_nome_custom") {
          atualizado.nome = String(value || "");
          atualizado.vacina_id = null;
        }

        if (dataAplicacao && vacinaId) {
          atualizado.data_revacinar = somarDias(dataAplicacao, getIntervalo(vacinaId));
        }

        return atualizado;
      }) as PetFormData["vacinas"]
    );
  };

  async function copiarLinkVeterinario() {
    try {
      if (!petId) {
        toast.error("Salve o pet primeiro para gerar o link.");
        return;
      }

      const resp = await fetch(`/pets/${petId}/link-assinatura-vet`, {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const data = await resp.json();

      if (!data?.url) throw new Error("URL não retornada");

      await navigator.clipboard.writeText(data.url);
      toast.success("Link copiado. Agora envie ao veterinário.");
    } catch (error) {
      console.error(error);
      toast.error("Não foi possível copiar o link.");
    }
  }

  async function salvarSecaoVacinas() {
    try {
      if (!petId) {
        toast.error("Salve o pet primeiro para depois salvar as vacinas.");
        return;
      }

      setSalvando(true);

      const token = (
        document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null
      )?.content;

      const payload = {
        vacinas: todasVacinas.map((v) => ({
          id: /^\d+$/.test(String(v.id)) ? Number(v.id) : null,
          vacina_id: v.vacina_id ?? null,
          vacina_nome_custom: (v.vacina_nome_custom || "").trim() || null,
          dose_num: Number(v.dose_num || 1),
          data_aplicacao: v.data_aplicacao || null,
          data_revacinar: v.data_revacinar || null,
          fabricante: v.fabricante || "",
          laboratorio: v.fabricante || "",
          lote: v.lote || "",
          via: v.via || "",
          peso: v.peso || "",
          veterinario: v.nome_veterinario || v.veterinario || "",
          _delete: (v as any)._delete ? 1 : 0,
        })),
      };

      const response = await fetch(`/pets/${petId}/vacinas/salvar`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { "X-CSRF-TOKEN": token } : {}),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || data?.message || "Erro ao salvar vacinas.");
      }

      const vacinasRetorno: VacinaEntry[] = Array.isArray(data?.vacinas)
        ? data.vacinas.map((v: any, index: number) => ({
            id: String(v.id ?? crypto.randomUUID()),
            vacina_id: v.vacina_id ?? null,
            vacina_nome_custom: v.vacina_nome_custom ?? null,
            nome: v.vacina_nome_custom ?? v.nome ?? "",
            dose_num: Number(v.dose_num ?? index + 1),
            data_aplicacao: v.data_aplicacao ?? "",
            data_revacinar: v.data_revacinar ?? "",
            fabricante: v.fabricante ?? "",
            lote: v.lote ?? "",
            via: v.via ?? "",
            peso: v.peso != null ? String(v.peso) : "",
            veterinario: v.veterinario ?? "",
            nome_veterinario: v.veterinario ?? "",
            validado: Number(v.validado ?? 0),
          }))
        : vacinasVisiveis;

      updateField("vacinas", vacinasRetorno as PetFormData["vacinas"]);
      toast.success("Vacinas salvas com sucesso.");
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message || "Erro ao salvar vacinas.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="gradient-primary px-6 py-5 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-primary-foreground flex items-center gap-2">
            <Syringe className="w-5 h-5" />
            Carteira de Vacinas
          </h2>
          <p className="text-primary-foreground/80 text-sm mt-1">
            Informe a data de aplicação. A próxima dose é calculada automaticamente.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            type="button"
            size="sm"
            onClick={adicionarVacina}
            className="bg-card/20 hover:bg-card/30 text-primary-foreground border border-primary-foreground/30 gap-2 font-semibold"
          >
            <Plus className="w-4 h-4" />
            Adicionar
          </Button>
        </div>
      </div>

      <div className="flex items-start gap-2 px-4 py-3 bg-amber-50 border-b border-amber-200">
        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-800">
          Registros preenchidos pelo tutor ficam aguardando validação veterinária.
        </p>
      </div>

      <div className="p-4">
        {vazio ? (
          <div className="text-center py-10 text-muted-foreground">
            <Syringe className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>Nenhuma vacina cadastrada.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {vacinasVisiveis.map((vacina, index) => {
              const vacinaId = String(vacina.id);
              const aberta = abertas.has(vacinaId);
              const status = getStatus(vacina.data_revacinar || "");
              const validada = Number(vacina.validado) === 1;
              const usandoOutra = getValorSelectVacina(vacina) === OPCAO_OUTRA;

              const temDadosCompletos = Boolean(
                (vacina.vacina_id || vacina.vacina_nome_custom) &&
                  vacina.data_aplicacao &&
                  vacina.fabricante?.trim() &&
                  vacina.lote?.trim() &&
                  vacina.via?.trim()
              );

              const statusFinal = validada
                ? "validada"
                : temDadosCompletos
                ? "preenchida"
                : "incompleta";

              return (
                <div
                  key={vacinaId}
                  className="border border-border rounded-xl overflow-hidden bg-white shadow-sm"
                >
                  <button
                    type="button"
                    onClick={() => toggleGrupo(vacinaId)}
                    className="w-full px-4 py-4 bg-white hover:bg-muted/30 transition-colors border-b"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-left">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-bold text-foreground text-base">
                            {getNomeVacina(vacina)}
                          </span>
                          <Badge variant={status.variant}>{status.text}</Badge>
                        </div>

                        <p className="text-xs text-muted-foreground mt-1">
                          {index + 1}ª dose
                        </p>
                      </div>

                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-sm text-blue-600">Ver/Editar</span>
                        {aberta ? (
                          <ChevronUp className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </button>

                  {aberta && (
                    <div className="p-4 space-y-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <h4 className="font-semibold text-foreground">
                            {getNomeVacina(vacina)}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {vacina.dose_num || index + 1}ª Dose
                          </p>
                        </div>

                        <div
                          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                            statusFinal === "validada"
                              ? "bg-green-100 text-green-700"
                              : statusFinal === "preenchida"
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {statusFinal === "validada"
                            ? "Validada"
                            : statusFinal === "preenchida"
                            ? "Aguardando validação"
                            : "Dados incompletos"}
                        </div>
                      </div>

                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                        <div>
                          <label className="text-sm font-medium text-foreground block mb-1">
                            Vacina
                          </label>
                          <select
                            value={getValorSelectVacina(vacina)}
                            onChange={(e) => {
                              const value = e.target.value;

                              if (value === OPCAO_OUTRA) {
                                atualizarVacina(vacinaId, "vacina_id", null);
                                atualizarVacina(vacinaId, "vacina_nome_custom", "");
                                return;
                              }

                              if (value === "") {
                                atualizarVacina(vacinaId, "vacina_id", null);
                                atualizarVacina(vacinaId, "vacina_nome_custom", null);
                                atualizarVacina(vacinaId, "nome", "");
                                return;
                              }

                              atualizarVacina(vacinaId, "vacina_id", Number(value));
                            }}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            disabled={validada}
                          >
                            <option value="">Selecione</option>
                            {catalogo.map((opcao) => (
                              <option key={opcao.id} value={String(opcao.id)}>
                                {opcao.nome}
                              </option>
                            ))}
                            <option value={OPCAO_OUTRA}>Outra</option>
                          </select>
                        </div>

                        {usandoOutra && (
                          <div className="md:col-span-2 lg:col-span-4">
                            <label className="text-sm font-medium text-foreground block mb-1">
                              Nome da vacina
                            </label>
                            <Input
                              value={vacina.vacina_nome_custom || ""}
                              onChange={(e) =>
                                atualizarVacina(
                                  vacinaId,
                                  "vacina_nome_custom",
                                  e.target.value
                                )
                              }
                              placeholder="Digite o nome da vacina"
                              disabled={validada}
                            />
                          </div>
                        )}

                        <div>
                          <label className="text-sm font-medium text-foreground block mb-1">
                            Dose
                          </label>
                          <Input
                            type="number"
                            min={1}
                            value={vacina.dose_num || ""}
                            onChange={(e) =>
                              atualizarVacina(
                                vacinaId,
                                "dose_num",
                                Number(e.target.value || 1)
                              )
                            }
                            disabled={validada}
                          />
                        </div>

                        <div>
                          <label className="text-sm font-medium text-foreground block mb-1">
                            Aplicação
                          </label>
                          <Input
                            type="date"
                            value={vacina.data_aplicacao || ""}
                            onChange={(e) =>
                              atualizarVacina(vacinaId, "data_aplicacao", e.target.value)
                            }
                            disabled={validada}
                          />
                        </div>

                        <div>
                          <label className="text-sm font-medium text-foreground block mb-1">
                            Data prevista
                          </label>
                          <Input
                            type="date"
                            value={vacina.data_revacinar || ""}
                            onChange={(e) =>
                              atualizarVacina(vacinaId, "data_revacinar", e.target.value)
                            }
                            disabled={validada}
                          />
                        </div>

                        <div>
                          <label className="text-sm font-medium text-foreground block mb-1">
                            Fabricante / Laboratório
                          </label>
                          <Input
                            value={vacina.fabricante || ""}
                            onChange={(e) =>
                              atualizarVacina(vacinaId, "fabricante", e.target.value)
                            }
                            placeholder="Ex: Zoetis"
                            disabled={validada}
                          />
                        </div>

                        <div>
                          <label className="text-sm font-medium text-foreground block mb-1">
                            Lote
                          </label>
                          <Input
                            value={vacina.lote || ""}
                            onChange={(e) =>
                              atualizarVacina(vacinaId, "lote", e.target.value)
                            }
                            placeholder="Ex: ABC123"
                            disabled={validada}
                          />
                        </div>

                        <div>
                          <label className="text-sm font-medium text-foreground block mb-1">
                            Via
                          </label>
                          <select
                            value={vacina.via || ""}
                            onChange={(e) =>
                              atualizarVacina(vacinaId, "via", e.target.value)
                            }
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            disabled={validada}
                          >
                            <option value="">Via</option>
                            <option value="injetavel">Injetável</option>
                            <option value="intranasal">Intranasal</option>
                            <option value="oral">Oral</option>
                            <option value="outra">Outra</option>
                          </select>
                        </div>

                        <div>
                          <label className="text-sm font-medium text-foreground block mb-1">
                            Peso (kg)
                          </label>
                          <Input
                            type="number"
                            step="0.01"
                            value={vacina.peso || ""}
                            onChange={(e) =>
                              atualizarVacina(vacinaId, "peso", e.target.value)
                            }
                            placeholder="Ex: 12.50"
                            disabled={validada}
                          />
                        </div>

                        <div className="md:col-span-2 lg:col-span-4">
                          <label className="text-sm font-medium text-foreground block mb-1">
                            Veterinário / CRMV
                          </label>
                          <Input
                            value={vacina.nome_veterinario || vacina.veterinario || ""}
                            onChange={(e) =>
                              atualizarVacina(
                                vacinaId,
                                "nome_veterinario",
                                e.target.value
                              )
                            }
                            placeholder="Nome do veterinário / CRMV"
                            disabled={validada}
                          />
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removerVacina(vacinaId)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          disabled={validada}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-4">
          <Button type="button" variant="outline" onClick={copiarLinkVeterinario}>
            Link para validação do veterinário
          </Button>

          <Button type="button" onClick={salvarSecaoVacinas} disabled={salvando}>
            <Save className="w-4 h-4 mr-2" />
            {salvando ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </div>
    </div>
  );
}